// Training Lab, live: add a document, VERA reads it, protects every person in it, and turns frameworks and governance
// into the lenses and rules every training example must follow (the control plane's TrainingLab). Eva and the psychologist only.
// Case material is sealed until it has been de-identified and approved; nothing is made from it before that.

import { useEffect, useRef, useState, type DragEvent } from "react";
import { Head, Icon, Orb, Pill, useStudio } from "./ui";
import { cpFetch } from "../lib/controlPlane";
import { ClearCase, DecodePanel, ReviewQueue } from "./TrainingReview";

type Kind = "framework" | "governance" | "case" | "knowledge" | "research";
type Reading = { suggestedKind: Kind; confidence: string; summary: string; domains: string[]; personalDetails: "none" | "some" | "many"; identifierKinds: string[]; why: string };
type Source = { id: string; name: string; kind: Kind; confirmed: boolean; status: "read" | "sealed" | "cleared" | "rulebook" | "knowledge"; uploadedBy: string; uploadedAt: string; words: number; reading: Reading; lenses: number; rules: number; confirmedBy?: string };
type LensView = { id: string; name: string; definition: string; lookFor: string[]; questions: string[]; cautions: string[]; sources: string[] };
type Rulebook = { domains: { domain: string; code: string; lenses: LensView[] }[]; rules: { text: string; strength: "never" | "must" | "prefer"; why: string; sources: string[] }[]; lensCount: number; ruleCount: number; sources: number };

const KINDS: { id: Kind; name: string; what: string; becomes: string }[] = [
  { id: "framework", name: "Framework", what: "A method, taxonomy or lens for understanding people.", becomes: "Becomes lenses and rules in VERA's rulebook." },
  { id: "governance", name: "Governance", what: "Rules, standards, consent, privacy, safety.", becomes: "Becomes rules every training example must follow." },
  { id: "case", name: "Case material", what: "A case study, formulation or clinician's notes about one person.", becomes: "Stays sealed until it's de-identified and a clinician approves it." },
  { id: "knowledge", name: "Knowledge", what: "Facts about products, companies or operations.", becomes: "Goes to VERA's knowledge, not training." },
  { id: "research", name: "Research", what: "A paper or study.", becomes: "Kept as background knowledge and evidence." },
];
const KIND_NAME = Object.fromEntries(KINDS.map((k) => [k.id, k.name])) as Record<Kind, string>;
const ACCEPT = ".docx,.pdf,.md,.txt,.pptx,.xlsx,.csv,.json,.jsonl";
const READING_STEPS = ["Reading the document…", "Understanding what it is…", "Checking that every person is protected…", "Mapping it to VERA's domains…"];

export function TrainingLabLive() {
  const { getToken, toast } = useStudio();
  const [tab, setTab] = useState<"documents" | "review" | "rulebook">("documents");
  const [sources, setSources] = useState<Source[] | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => cpFetch<{ sources: Source[] }>(getToken, "/v1/training/sources").then((r) => { setSources(r.sources); setError(""); }).catch((e: Error) => setError(e.message));
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (!uploading) return;
    setStep(0);
    const t = window.setInterval(() => setStep((s) => Math.min(s + 1, READING_STEPS.length - 1)), 2600);
    return () => window.clearInterval(t);
  }, [uploading]);

  async function upload(files: FileList | File[]) {
    for (const f of Array.from(files)) {
      if (f.size > 20 * 1024 * 1024) { toast(`${f.name} is larger than 20 MB.`); continue; }
      setUploading(f.name);
      try {
        const data = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
          r.onerror = () => reject(new Error("The file couldn't be read."));
          r.readAsDataURL(f);
        });
        const s = await cpFetch<Source>(getToken, "/v1/training/sources", { method: "POST", body: JSON.stringify({ name: f.name, mimeType: f.type, data }) });
        setSources((list) => [s, ...(list ?? [])]);
        setOpen(s.id);
        toast(s.kind === "case" ? `${f.name} is sealed: it holds material about a person.` : `VERA read ${f.name}. Please confirm what it is.`);
      } catch (e) {
        toast(e instanceof Error ? e.message : "The document couldn't be added.");
      }
    }
    setUploading(null);
  }

  function onDrop(e: DragEvent) { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length) void upload(e.dataTransfer.files); }
  const list = sources ?? [];
  const lenses = list.reduce((n, s) => n + s.lenses, 0), rules = list.reduce((n, s) => n + s.rules, 0), sealed = list.filter((s) => s.status === "sealed").length;
  const current = list.find((s) => s.id === open) ?? null;

  return (
    <>
      <Head title="Training Lab" lead="Where VERA's mind is shaped. Add a document: VERA reads it, protects every person in it, and turns your frameworks into the lenses and rules every training example must follow." />
      <div className="grid g4">
        <div className="card stat"><b className="num">{list.length}</b><span>Documents</span></div>
        <div className="card stat"><b className="num">{lenses}</b><span>Lenses from your frameworks</span></div>
        <div className="card stat"><b className="num">{rules}</b><span>Rules from frameworks and governance</span></div>
        <div className="card stat"><b className="num">{sealed}</b><span>Case documents sealed</span></div>
      </div>
      <div className="tabs" role="tablist" style={{ maxWidth: 560 }}>
        <button role="tab" aria-selected={tab === "documents"} className={tab === "documents" ? "on" : ""} onClick={() => setTab("documents")}>Documents</button>
        <button role="tab" aria-selected={tab === "review"} className={tab === "review" ? "on" : ""} onClick={() => setTab("review")}>Review</button>
        <button role="tab" aria-selected={tab === "rulebook"} className={tab === "rulebook" ? "on" : ""} onClick={() => setTab("rulebook")}>VERA's rulebook</button>
      </div>
      {error && <div className="notice"><span>{error}</span></div>}
      {tab === "rulebook" ? <RulebookView /> : tab === "review" ? <ReviewQueue /> : (
        <div className="split">
          <div className="stack">
            <div className={`card tl-drop ${dragging ? "on" : ""}`} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
              role="button" tabIndex={0} onClick={() => !uploading && fileRef.current?.click()} onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) fileRef.current?.click(); }}>
              {uploading ? (<><Orb size={34} mode="think" /><b>{uploading}</b><span className="mute">{READING_STEPS[step]}</span></>)
                : (<><Icon name="docai" size={26} /><b>Add a document</b><span className="mute">Drop it here or choose a file: Word, PDF, Markdown, text, PowerPoint or Excel, up to 20 MB.</span>
                  <span className="faint" style={{ fontSize: 12 }}>VERA keeps the text, never the file. Names never appear in anything she writes about it.</span></>)}
              <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ""; }} />
            </div>
            <div className="card"><h2 style={{ marginBottom: 6 }}>Documents</h2>
              {sources === null && !error ? <p className="mute">Loading…</p> : !list.length ? <p className="mute">Nothing yet. Start with a framework, for example the Clinical Case Formulation Framework.</p> : (
                <div className="list">{list.map((s) => (
                  <button key={s.id} className={`li tl-row ${s.id === open ? "on" : ""}`} onClick={() => setOpen(s.id)}>
                    <span className="ico"><Icon name={s.status === "sealed" ? "lock" : s.kind === "framework" ? "sparkle" : s.kind === "governance" ? "shield" : "doc"} /></span>
                    <div className="grow" style={{ textAlign: "left" }}><b>{s.name}</b>
                      <div className="faint" style={{ fontSize: 12 }}>{KIND_NAME[s.kind]}{s.confirmed ? "" : " (VERA's suggestion)"} · {s.words.toLocaleString()} words{s.status === "rulebook" ? ` · ${s.lenses} lenses, ${s.rules} rules` : ""}</div></div>
                    {s.status === "sealed" ? <Pill tone="warn">Sealed</Pill> : s.status === "cleared" ? <Pill tone="ok">Cleared</Pill> : !s.confirmed ? <Pill tone="info">To confirm</Pill> : s.status === "rulebook" ? <Pill tone="ok">In rulebook</Pill> : <Pill tone="ok">Confirmed</Pill>}
                  </button>))}</div>)}
            </div>
          </div>
          {current ? <SourceDetail key={current.id} source={current} onChange={(s) => setSources((l) => (l ?? []).map((x) => (x.id === s.id ? s : x)))} onRemoved={() => { setSources((l) => (l ?? []).filter((x) => x.id !== current.id)); setOpen(null); }} onCleared={() => void load()} onDecoded={() => setTab("review")} />
            : <div className="card stack"><h2>How it works</h2>
                <ol className="tl-steps">
                  <li><b>Add</b> a document. VERA reads it and suggests what it is.</li>
                  <li><b>Protect.</b> She checks for names, dates and other details about people. Case material is sealed.</li>
                  <li><b>Confirm</b> what it is. Frameworks and governance become VERA's rulebook: the lenses she reads people through, and the rules every training example must follow.</li>
                  <li><b>Clear and decode.</b> A de-identified case is cleared, and VERA writes training conversations from it, never copying it.</li>
                  <li><b>Review</b> together: every VERA turn approved, edited or rejected, the five risks scored, and the outcome. The psychologist decides where you differ.</li>
                  <li><b>Next</b> (coming): the dataset, training VERA Core, and before/after comparison.</li>
                </ol></div>}
        </div>
      )}
    </>
  );
}

function SourceDetail({ source, onChange, onRemoved, onCleared, onDecoded }: { source: Source; onChange: (s: Source) => void; onRemoved: () => void; onCleared: () => void; onDecoded: () => void }) {
  const { getToken, toast } = useStudio();
  const [kind, setKind] = useState<Kind>(source.kind);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const r = source.reading;
  const sealed = source.status === "sealed";

  async function confirm() {
    setBusy(true);
    try {
      const s = await cpFetch<Source>(getToken, `/v1/training/sources/${source.id}/kind`, { method: "POST", body: JSON.stringify({ kind }) });
      onChange(s);
      toast(s.status === "rulebook" ? `Added to VERA's rulebook: ${s.lenses} lenses and ${s.rules} rules.` : s.status === "sealed" ? "Sealed. It waits for de-identification." : "Confirmed.");
    } catch (e) { toast(e instanceof Error ? e.message : "Couldn't confirm it."); }
    finally { setBusy(false); }
  }
  async function showText() {
    try { setText((await cpFetch<{ text: string; truncated: boolean }>(getToken, `/v1/training/sources/${source.id}`)).text); }
    catch (e) { toast(e instanceof Error ? e.message : "Couldn't open it."); }
  }
  async function remove() {
    if (!window.confirm(`Remove "${source.name}" from the Training Lab? Its text and any rules it added are deleted.`)) return;
    try { await cpFetch(getToken, `/v1/training/sources/${source.id}`, { method: "DELETE" }); onRemoved(); toast("Removed."); }
    catch (e) { toast(e instanceof Error ? e.message : "Couldn't remove it."); }
  }

  const willBuild = kind === "framework" || kind === "governance";
  return (
    <div className="card stack" style={{ gap: 14 }}>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "nowrap", gap: 10 }}>
        <h2 style={{ fontSize: 18, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{source.name}</h2>
        <button className="btn" onClick={() => void remove()} title="Remove"><Icon name="x" size={14} /></button>
      </div>
      {r.summary && <p style={{ margin: 0 }}>{r.summary}</p>}
      {r.domains.length > 0 && <div className="row" style={{ gap: 6 }}>{r.domains.map((d) => <span className="chip" key={d}>{d}</span>)}</div>}

      {r.personalDetails !== "none" && (
        <div className={`notice ${sealed ? "" : "calm"}`}><Icon name="lock" size={16} />
          <span style={{ fontSize: 13 }}><b>Personal details: {r.personalDetails}.</b>{r.identifierKinds.length ? ` Found ${r.identifierKinds.join(", ")}.` : ""} {sealed
            ? "This document is sealed. Nothing is made from it until it has been de-identified and cleared."
            : "If this is material about a real person, mark it as Case material so it's sealed."}</span></div>)}

      <div className="stack" style={{ gap: 8 }}>
        <span className="label">What is it?{!source.confirmed && r.why ? <span className="faint" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}> · VERA thinks {KIND_NAME[r.suggestedKind].toLowerCase()} ({r.confidence}): {r.why}</span> : null}</span>
        <div className="tl-kinds">{KINDS.map((k) => (
          <label key={k.id} className={`tl-kind ${kind === k.id ? "on" : ""}`}>
            <input type="radio" name={`kind-${source.id}`} checked={kind === k.id} onChange={() => setKind(k.id)} />
            <span><b>{k.name}</b><small>{k.what} {k.becomes}</small></span>
          </label>))}</div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn primary" disabled={busy || (source.confirmed && kind === source.kind && source.status !== "read")} onClick={() => void confirm()}>
            {busy ? (willBuild ? "Building VERA's rulebook… this can take a minute" : "Saving…") : source.confirmed && kind === source.kind ? (willBuild && source.status !== "rulebook" ? "Build rulebook" : "Confirmed") : willBuild ? "Confirm and build rulebook" : "Confirm"}
          </button>
          {source.confirmed && <span className="faint" style={{ fontSize: 12 }}>Confirmed by {source.confirmedBy ?? "you"}.</span>}
        </div>
      </div>

      {source.kind === "case" && source.confirmed && (source.status === "sealed" ? <ClearCase sourceId={source.id} onCleared={onCleared} /> : source.status === "cleared" ? <DecodePanel sourceId={source.id} onMade={onDecoded} /> : null)}

      <div className="stack" style={{ gap: 6 }}>
        {text === null
          ? <button className="btn" onClick={() => void showText()}>{sealed ? "Show the text (opening is logged)" : "Show the text VERA read"}</button>
          : <pre className="tl-text">{text}</pre>}
        <span className="faint" style={{ fontSize: 12 }}>Added by {source.uploadedBy} · {new Date(source.uploadedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · VERA kept the text only, not the file.</span>
      </div>
    </div>
  );
}

function RulebookView() {
  const { getToken } = useStudio();
  const [book, setBook] = useState<Rulebook | null>(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { cpFetch<Rulebook>(getToken, "/v1/training/rulebook").then(setBook).catch((e: Error) => setError(e.message)); }, [getToken]);
  if (error) return <div className="notice"><span>{error}</span></div>;
  if (!book) return <p className="mute">Loading VERA's rulebook…</p>;
  if (!book.sources) return <div className="card stack"><h2>VERA's rulebook is empty</h2><p className="mute">Add a framework or governance document and confirm it. VERA turns it into lenses (how she reads people) and rules (what every training example must do).</p></div>;
  const match = (s: string) => !q || s.toLowerCase().includes(q.toLowerCase());
  const strengthName = { never: "Never", must: "Always", prefer: "Prefer" } as const;
  return (
    <div className="split">
      <div className="stack">
        <label className="search" htmlFor="rq"><Icon name="search" size={14} /><input id="rq" placeholder="Search lenses" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" /></label>
        {book.domains.map((d) => {
          const shown = d.lenses.filter((l) => match(l.name) || match(l.definition));
          if (!shown.length) return null;
          return (
            <div className="card" key={d.code}>
              <div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 17 }}>{d.domain}</h2><span className="faint" style={{ fontSize: 12 }}>{d.code} · {d.lenses.length} lens{d.lenses.length === 1 ? "" : "es"}</span></div>
              {shown.map((l) => (
                <details className="tl-lens" key={l.id}>
                  <summary><span className="tl-code">{l.id}</span><b>{l.name}</b></summary>
                  {l.definition && <p>{l.definition}</p>}
                  {l.lookFor.length > 0 && <><span className="label">Look for</span><ul>{l.lookFor.map((x) => <li key={x}>{x}</li>)}</ul></>}
                  {l.questions.length > 0 && <><span className="label">Questions VERA can ask</span><ul>{l.questions.map((x) => <li key={x}>{x}</li>)}</ul></>}
                  {l.cautions.length > 0 && <><span className="label">Cautions</span><ul>{l.cautions.map((x) => <li key={x}>{x}</li>)}</ul></>}
                  <span className="faint" style={{ fontSize: 12 }}>From {l.sources.join(", ")}</span>
                </details>))}
            </div>);
        })}
      </div>
      <div className="card stack" style={{ gap: 10 }}>
        <h2>Rules</h2>
        <p className="mute" style={{ margin: 0 }}>Every training example must follow these. {book.ruleCount} rules from {book.sources} document{book.sources === 1 ? "" : "s"}.</p>
        {book.rules.map((r) => (
          <div className="tl-rule" key={r.text}>
            <Pill tone={r.strength === "never" ? "bad" : r.strength === "must" ? "ok" : "info"}>{strengthName[r.strength]}</Pill>
            <div><div>{r.text}</div>{r.why && <div className="faint" style={{ fontSize: 12 }}>{r.why}</div>}</div>
          </div>))}
      </div>
    </div>
  );
}
